import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { SfLog } from '@app/adapters/logger'
import { useToastStore } from '@app/shared/pinia/toast'
import {
  createMetricRequest,
  fetchDrawerStateRequest,
  reorderHighlightedRequest,
  reorderInsightRequest,
  setHighlightedRequest,
  setPinnedRequest,
  setPinToTopRequest,
} from '@app/application/report/api/drawer'
import type { CreateMetricInput } from '@app/application/report/api/drawer'
import type {
  DrawerState,
  DrawerGraphEntry,
} from '@app/application/report/types'
import type { Locale } from '@app/shared/types/locale'
import { persistReportGraphsDrawerOpen } from '@app/shared/api/user'

function extractServerErrorMessage(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null
  const maybe = error as {
    body?: { errors?: Record<string, string[] | undefined> }
  }
  const errors = maybe.body?.errors
  if (!errors) return null
  for (const key of Object.keys(errors)) {
    const messages = errors[key]
    if (messages && messages.length > 0) return messages[0]
  }
  return null
}

export const HIGHLIGHTED_LIMIT = 3

export const useReportGraphsDrawer = defineStore('reportGraphsDrawer', () => {
  const I18n = window.I18n
  const toastStore = useToastStore()

  const reportTemplateId = ref<number>(0)
  const locale = ref<Locale>('en')
  const pinned = ref<DrawerGraphEntry | null>(null)
  const highlighted = ref<DrawerGraphEntry[]>([])
  const insight = ref<DrawerGraphEntry[]>([])
  const fullscreen = ref(false)
  const pinToTop = ref(false)
  const locked = ref(false)
  // Whether the pin can be toggled on this report; computed server-side.
  const pinnable = ref(false)
  // The pin is forced on and the toggle is display-only; computed server-side.
  const pinLocked = ref(false)
  const isOpen = ref(true)

  const highlightedCount = computed(() => highlighted.value.length)
  const canHighlight = computed(
    () => highlighted.value.length < HIGHLIGHTED_LIMIT
  )

  function openFullscreen(): void {
    fullscreen.value = true
  }

  function closeFullscreen(): void {
    fullscreen.value = false
  }

  function openDrawer(): void {
    if (isOpen.value) return
    isOpen.value = true
    void persistReportGraphsDrawerOpen(true)
  }

  function closeDrawer(): void {
    if (!isOpen.value) return
    isOpen.value = false
    fullscreen.value = false
    void persistReportGraphsDrawerOpen(false)
  }

  function toggleDrawer(): void {
    if (isOpen.value) {
      closeDrawer()
    } else {
      openDrawer()
    }
  }

  function hydrate(params: {
    reportTemplateId: number
    locale: Locale
    state: DrawerState
    pinToTop?: boolean
    locked?: boolean
    pinnable?: boolean
    pinLocked?: boolean
    isOpen?: boolean
  }): void {
    reportTemplateId.value = params.reportTemplateId
    locale.value = params.locale
    pinToTop.value = params.pinToTop ?? false
    locked.value = params.locked ?? false
    pinnable.value = params.pinnable ?? false
    pinLocked.value = params.pinLocked ?? false
    isOpen.value = params.isOpen ?? true
    patch(params.state)
  }

  async function setPinToTop(target: boolean): Promise<void> {
    const previous = pinToTop.value
    pinToTop.value = target

    try {
      await setPinToTopRequest({
        reportTemplateId: reportTemplateId.value,
        pinToTop: target,
      })
    } catch (error) {
      pinToTop.value = previous
      raisePinToTopErrorToast(error)
    }
  }

  function raisePinToTopErrorToast(error: unknown): void {
    const serverMessage = extractServerErrorMessage(error)
    const message =
      serverMessage ?? I18n.t('report.drawer.pin_to_top.toggle_error')

    toastStore.raiseToast({
      id: 'drawer-pin-to-top-error',
      title: I18n.t('report.drawer.pin_to_top.toggle_error_title'),
      message,
      variant: 'danger',
      size: 'md',
      actions: [],
    })

    SfLog.error('Report pin-to-top toggle failed', {
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }

  function patch(next: DrawerState): void {
    pinned.value = next.pinned
    highlighted.value = next.highlighted
    insight.value = next.insight
  }

  function snapshot(): DrawerState {
    return {
      pinned: pinned.value,
      highlighted: [...highlighted.value],
      insight: [...insight.value],
    }
  }

  function applyHighlightedOptimistic(id: number, target: boolean): void {
    if (target) {
      const moving = insight.value.find((g) => g.id === id)
      if (!moving) return
      insight.value = insight.value.filter((g) => g.id !== id)
      highlighted.value = [...highlighted.value, moving]
      return
    }

    const moving = highlighted.value.find((g) => g.id === id)
    if (!moving) return
    highlighted.value = highlighted.value.filter((g) => g.id !== id)
    insight.value = [...insight.value, moving]
  }

  function applyPinnedOptimistic(id: number, target: boolean): void {
    if (target) {
      const moving =
        highlighted.value.find((g) => g.id === id) ??
        insight.value.find((g) => g.id === id)
      if (!moving) return

      const existing = pinned.value
      if (existing && existing.id !== id) {
        insight.value = [...insight.value, existing]
      }
      highlighted.value = highlighted.value.filter((g) => g.id !== id)
      insight.value = insight.value.filter((g) => g.id !== id)
      pinned.value = moving
      return
    }

    const moving = pinned.value && pinned.value.id === id ? pinned.value : null
    if (!moving) return
    pinned.value = null
    insight.value = [...insight.value, moving]
  }

  async function setHighlighted(id: number, target: boolean): Promise<void> {
    const previous = snapshot()
    applyHighlightedOptimistic(id, target)

    try {
      const next = await setHighlightedRequest({
        reportTemplateId: reportTemplateId.value,
        graphId: id,
        highlighted: target,
      })
      patch(next)
    } catch (error) {
      patch(previous)
      raiseToggleErrorToast(id, error)
    }
  }

  async function refresh(): Promise<void> {
    const next = await fetchDrawerStateRequest({
      reportTemplateId: reportTemplateId.value,
    })
    patch(next)
  }

  // Throws on validation error so the caller (the modal) can render per-field
  // errors. Network/5xx errors raise a toast and re-throw.
  async function createMetric(input: CreateMetricInput): Promise<void> {
    try {
      await createMetricRequest({
        reportTemplateId: reportTemplateId.value,
        locale: locale.value,
        input,
      })
    } catch (error) {
      const status =
        typeof error === 'object' && error !== null && 'status' in error
          ? (error as { status: number }).status
          : null
      if (status !== 422) {
        raiseCreateMetricErrorToast(error)
      }
      throw error
    }

    await refresh()
  }

  function raiseCreateMetricErrorToast(error: unknown): void {
    const serverMessage = extractServerErrorMessage(error)
    const message =
      serverMessage ?? I18n.t('report.drawer.insight.create_error')

    toastStore.raiseToast({
      id: 'drawer-create-metric-error',
      message,
      variant: 'danger',
      size: 'md',
      actions: [],
    })

    SfLog.error('Report graphs drawer create metric failed', {
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }

  async function setPinned(id: number, target: boolean): Promise<void> {
    const previous = snapshot()
    applyPinnedOptimistic(id, target)

    try {
      const next = await setPinnedRequest({
        reportTemplateId: reportTemplateId.value,
        graphId: id,
        pinned: target,
      })
      patch(next)
    } catch (error) {
      patch(previous)
      raiseToggleErrorToast(id, error)
    }
  }

  function applyReorderOptimistic(orderedIds: number[]): void {
    const byId = new Map(highlighted.value.map((entry) => [entry.id, entry]))
    highlighted.value = orderedIds
      .map((id) => byId.get(id))
      .filter((entry): entry is DrawerGraphEntry => entry !== undefined)
  }

  async function reorderHighlighted(orderedIds: number[]): Promise<void> {
    const previous = snapshot()
    applyReorderOptimistic(orderedIds)

    try {
      const next = await reorderHighlightedRequest({
        reportTemplateId: reportTemplateId.value,
        orderedIds,
      })
      patch(next)
    } catch (error) {
      patch(previous)
      raiseReorderErrorToast(error)
    }
  }

  function applyInsightReorderOptimistic(orderedIds: number[]): void {
    const byId = new Map(insight.value.map((entry) => [entry.id, entry]))
    insight.value = orderedIds
      .map((id) => byId.get(id))
      .filter((entry): entry is DrawerGraphEntry => entry !== undefined)
  }

  async function reorderInsight(orderedIds: number[]): Promise<void> {
    const previous = snapshot()
    applyInsightReorderOptimistic(orderedIds)

    try {
      const next = await reorderInsightRequest({
        reportTemplateId: reportTemplateId.value,
        orderedIds,
      })
      patch(next)
    } catch (error) {
      patch(previous)
      raiseInsightReorderErrorToast(error)
    }
  }

  function raiseReorderErrorToast(error: unknown): void {
    const serverMessage = extractServerErrorMessage(error)
    const message =
      serverMessage ?? I18n.t('report.drawer.highlighted.reorder_error')

    toastStore.raiseToast({
      id: 'drawer-reorder-error',
      message,
      variant: 'danger',
      size: 'md',
      actions: [],
    })

    SfLog.error('Report graphs drawer reorder failed', {
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }

  function raiseInsightReorderErrorToast(error: unknown): void {
    const serverMessage = extractServerErrorMessage(error)
    const message =
      serverMessage ?? I18n.t('report.drawer.insight.reorder_error')

    toastStore.raiseToast({
      id: 'drawer-insight-reorder-error',
      message,
      variant: 'danger',
      size: 'md',
      actions: [],
    })

    SfLog.error('Report graphs drawer insight reorder failed', {
      error: error instanceof Error ? error : new Error(String(error)),
    })
  }

  function raiseToggleErrorToast(id: number, error: unknown): void {
    const serverMessage = extractServerErrorMessage(error)
    const message =
      serverMessage ?? I18n.t('report.drawer.highlighted.toggle_error')

    toastStore.raiseToast({
      id: `drawer-toggle-error-${id}`,
      message,
      variant: 'danger',
      size: 'md',
      actions: [],
    })

    SfLog.error('Report graphs drawer toggle failed', {
      error: error instanceof Error ? error : new Error(String(error)),
      extra: { graphId: id },
    })
  }

  return {
    reportTemplateId,
    locale,
    pinned,
    highlighted,
    insight,
    fullscreen,
    pinToTop,
    locked,
    pinnable,
    pinLocked,
    isOpen,
    highlightedCount,
    canHighlight,
    hydrate,
    patch,
    refresh,
    createMetric,
    setHighlighted,
    setPinned,
    setPinToTop,
    reorderHighlighted,
    reorderInsight,
    openFullscreen,
    closeFullscreen,
    openDrawer,
    closeDrawer,
    toggleDrawer,
  }
})
